import { FC, useCallback, useState } from 'react';
import { Button, Drawer, Form } from 'antd';
import { Icon } from '@fuxi/eevee-icon';
import SlideRulerModule from '@/components/slide-ruler';
import cx from './index.module.scss';

type BMIInfo = {
  height: number;
  weight: number;
  bodyFatRate: number;
};

export interface BMIInfoProps {
  value?: BMIInfo;
  onChange?: (value: BMIInfoProps['value']) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}

type SlideRulerItem = {
  defaultValue: number;
  label: string;
  unit: string;
  name: 'height' | 'weight' | 'bodyFatRate';
  maxValue?: number;
  minValue?: number;
};

const SLIDE_RULER_OPTIONS: SlideRulerItem[] = [
  {
    label: '身高',
    unit: '厘米',
    name: 'height',
    defaultValue: 160,
    minValue: 50,
    maxValue: 250,
  },
  {
    label: '体重',
    unit: '公斤',
    name: 'weight',
    defaultValue: 60,
    minValue: 20,
    maxValue: 150,
  },
  {
    label: '体脂率',
    unit: '%',
    name: 'bodyFatRate',
    defaultValue: 25,
    minValue: 5,
    maxValue: 50,
  },
];

const BMIInfo: FC<BMIInfoProps> = props => {
  const { value, onChange, placeholder, className } = props;
  const [isDrawerShow, setIsDrawerShow] = useState<boolean>(false);

  const [form] = Form.useForm();

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerShow(false);
    onChange(form.getFieldsValue());
  }, [form, onChange]);

  return (
    <div className={cx('bmi-info', className)}>
      <div
        className={cx('bmi-info-input')}
        onClick={() => {
          setIsDrawerShow(true);
          form.setFieldsValue(value);
        }}
      >
        <div className={cx('value')}>
          {value?.height || value?.weight || value?.bodyFatRate ? (
            <div className={cx('text')}>
              <span>身高 </span>
              <span>{value?.height}厘米</span>
              <span>｜</span>
              <span>体重 </span>
              <span>{value?.weight}公斤</span>
              <span>｜</span>
              <span>体脂率 </span>
              <span>{value?.bodyFatRate}%</span>
            </div>
          ) : (
            <div className={cx('placeholder')}>{placeholder}</div>
          )}
        </div>
        <Icon name="body-data" className={cx('icon')} color="#000000" />
      </div>
      <Drawer
        className={cx('slide-ruler-drawer')}
        onClose={() => setIsDrawerShow(false)}
        width="520px"
        destroyOnClose={true}
        title={<span className={cx('drawer-title')}>选择身高/体重/体脂率</span>}
        closeIcon={<Icon name="close-circle" className={cx('close-drawer')} color="#b3b4b6" />}
        open={isDrawerShow}
      >
        <div className={cx('bmi-info-content')}>
          <Form form={form}>
            <div className={cx('slide-ruler-box')}>
              {SLIDE_RULER_OPTIONS?.map((item, index) => (
                <div key={index} className={cx('slide-ruler-module')}>
                  <Form.Item name={item.name} noStyle>
                    <SlideRulerModule
                      label={item.label}
                      unit={item.unit}
                      defaultVal={item.defaultValue}
                      maxValue={item.maxValue}
                      minValue={item.minValue}
                    />
                  </Form.Item>
                </div>
              ))}
            </div>
          </Form>

          <div className={cx('note-text')}>为提升人体识别确切性，优化顾客体感，请准确选择上述身体信息。</div>

          <div className={cx('slide-ruler-footer')}>
            <Button color="primary" className={cx('confirm-btn')} onClick={handleCloseDrawer}>
              确定
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default BMIInfo;
